import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from './cn';

export type ButtonVariant = 'primary' | 'outline' | 'ghost' | 'danger' | 'gold';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const base =
  'inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-colors transition-shadow disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#1A1A1A] focus-visible:ring-offset-white';

const variants: Record<ButtonVariant, string> = {
  primary:
    'bg-[#1A1A1A] text-[#F4D03F] shadow-md hover:bg-black hover:shadow-lg',
  outline:
    'border border-[#3D3328]/20 bg-white text-[#1A1A1A] hover:bg-[#FAF8F5]',
  ghost: 'text-[#3D3328] hover:text-[#1A1A1A] hover:bg-[#FAF8F5]',
  danger: 'bg-rose-600 text-white hover:bg-rose-700 shadow-md',
  gold:
    'bg-[#EDB24A] text-[#0A1628] hover:bg-[#FAD98A] shadow-md hover:shadow-lg',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', type, ...props }, ref) => {
    return (
      <button
        ref={ref}
        type={type ?? 'button'}
        className={cn(base, variants[variant], sizes[size], className)}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';
